import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  ApiResponseEnvelope,
  PaginatedListResult,
  PaginatedQueryOptions,
} from '@doctor-scheduling/shared/models';
import {
  AppointmentMedia,
  AppointmentStatus,
  AppointmentType,
  InviteStatus,
  RecurrenceType,
  SeriesUpdateType,
} from '@doctor-scheduling/shared/constants';
import { API_BASE_URL } from './api-base-url.token';
import { buildListHttpParams } from './list-query.util';

/** Patient RSVP invite on an appointment payload. */
export interface AppointmentInviteItem {
  id: string;
  appointmentId: string;
  email: string;
  status: InviteStatus;
  expiresAt: string;
  respondedAt?: string | null;
}

/** Appointment row returned by list and detail endpoints. */
export interface AppointmentListItem {
  id: string;
  clinicId: string;
  doctorId: string;
  patientId: string;
  seriesId?: string | null;
  title?: string | null;
  appointmentDate: string;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  type: AppointmentType;
  media?: AppointmentMedia;
  mediaLink?: string | null;
  location?: string | null;
  reminderMinutes?: number | null;
  reason: string | null;
  notes: string | null;
  cancellationReason: string | null;
  doctor: {
    id: string;
    specialization: string;
    user: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
  };
  patient: {
    id: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string | null;
  };
  /** RSVP invites sent for this appointment (newest last from API). */
  invites?: AppointmentInviteItem[];
}

/**
 * Latest patient RSVP invite status for an appointment.
 * @param appt - Appointment row with optional invites
 * @returns Invite status string, or null when no invite was sent
 */
export function latestInviteStatus(appt: AppointmentListItem): string | null {
  const invites = appt.invites;
  if (!invites?.length) return null;
  const responded = [...invites]
    .filter((i) => i.respondedAt)
    .sort((x, y) => String(y.respondedAt).localeCompare(String(x.respondedAt)));
  if (responded[0]) return responded[0].status;
  return invites[invites.length - 1].status;
}

/** Bookable time window with availability flag. */
export interface TimeSlot {
  startTime: string;
  endTime: string;
  available: boolean;
}

/** Payload for creating a new appointment (optionally recurring). */
export interface CreateAppointmentPayload {
  doctorId: string;
  patientId: string;
  appointmentDate: string;
  startTime: string;
  endTime: string;
  title?: string;
  type?: AppointmentType;
  media?: AppointmentMedia;
  mediaLink?: string | null;
  location?: string | null;
  reminderMinutes?: number | null;
  reason?: string;
  notes?: string;
  sendInvite?: boolean;
  recurrence?: RecurrenceType;
  recurrenceCount?: number;
  occurrences?: string[];
  recurrenceStartDate?: string;
  recurrenceEndDate?: string | null;
}

/** Payload for updating an appointment. */
export interface UpdateAppointmentPayload extends Partial<CreateAppointmentPayload> {
  updateType?: SeriesUpdateType;
}

/** List query combining Eaze pagination with appointment shortcuts. */
export interface AppointmentFilters extends PaginatedQueryOptions {
  doctorId?: string;
  doctorIds?: string;
  patientId?: string;
  status?: AppointmentStatus | string;
  date?: string;
  fromDate?: string;
  toDate?: string;
}

/** HTTP client for appointment CRUD and slot lookup. */
@Injectable({
  providedIn: 'root',
})
export class AppointmentApi {
  /** Angular HTTP client used for API requests. */
  private readonly http = inject(HttpClient);
  /** NestJS API origin from the web app environment. */
  private readonly apiBaseUrl = inject(API_BASE_URL);
  /** Base URL for appointment routes under `/api/v1/appointments`. */
  private readonly baseUrl = `${this.apiBaseUrl}/api/v1/appointments`;

  /**
   * Lists appointments with Eaze-style page/sort/filter plus shortcut filters.
   */
  findAll(
    filters?: AppointmentFilters
  ): Observable<ApiResponseEnvelope<PaginatedListResult<AppointmentListItem>>> {
    return this.http.get<ApiResponseEnvelope<PaginatedListResult<AppointmentListItem>>>(
      this.baseUrl,
      { params: buildListHttpParams(filters as Record<string, unknown>) }
    );
  }

  findOne(id: string): Observable<ApiResponseEnvelope<AppointmentListItem>> {
    return this.http.get<ApiResponseEnvelope<AppointmentListItem>>(`${this.baseUrl}/${id}`);
  }

  getAvailableSlots(doctorId: string, date: string): Observable<ApiResponseEnvelope<TimeSlot[]>> {
    return this.http.get<ApiResponseEnvelope<TimeSlot[]>>(
      `${this.baseUrl}/slots/${doctorId}`,
      { params: { date } }
    );
  }

  create(data: CreateAppointmentPayload): Observable<ApiResponseEnvelope<AppointmentListItem>> {
    return this.http.post<ApiResponseEnvelope<AppointmentListItem>>(this.baseUrl, data);
  }

  update(
    id: string,
    data: UpdateAppointmentPayload
  ): Observable<ApiResponseEnvelope<AppointmentListItem>> {
    return this.http.put<ApiResponseEnvelope<AppointmentListItem>>(`${this.baseUrl}/${id}`, data);
  }

  updateStatus(
    id: string,
    data: {
      status: AppointmentStatus;
      cancellationReason?: string;
      notes?: string;
    }
  ): Observable<ApiResponseEnvelope<AppointmentListItem>> {
    return this.http.put<ApiResponseEnvelope<AppointmentListItem>>(
      `${this.baseUrl}/${id}/status`,
      data
    );
  }

  delete(
    id: string,
    updateType: SeriesUpdateType = SeriesUpdateType.SINGLE,
    cancellationReason?: string
  ): Observable<ApiResponseEnvelope<{ success: boolean }>> {
    let params = new HttpParams().set('updateType', updateType);
    if (cancellationReason) {
      params = params.set('cancellationReason', cancellationReason);
    }
    return this.http.delete<ApiResponseEnvelope<{ success: boolean }>>(`${this.baseUrl}/${id}`, {
      params,
    });
  }
}
